// Turn a landscape recording into a 9:16 reel (1080×1920) in the browser:
// the clip is replayed into a vertical canvas (with its sound) and recorded
// again — MP4 where the browser can record it, otherwise WebM. Takes about as
// long as the clip itself.
//   fill: centre-crop to fill the phone screen
//   fit:  the whole frame, over a blurred, darkened copy of itself
import { baseMime, pickRecordingMime } from '../services/recordingShare';

export type ReelMode = 'fill' | 'fit';
export interface ReelResult { blob: Blob; mime: string }

const W = 1080, H = 1920;

export async function makeReel(
  src: Blob,
  opts: { mode: ReelMode; durationS: number; title?: string; onProgress?: (pct: number) => void; signal?: AbortSignal },
): Promise<ReelResult> {
  const url = URL.createObjectURL(src);
  const video = document.createElement('video');
  video.src = url; video.playsInline = true; video.preload = 'auto'; video.crossOrigin = 'anonymous';
  await new Promise<void>((res, rej) => { video.onloadedmetadata = () => res(); video.onerror = () => rej(new Error('Could not read the recording')); });

  const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d')!;
  const stream = canvas.captureStream(30);

  // Sound: route the clip's audio into the recording instead of the speakers.
  let audioCtx: AudioContext | null = null;
  try {
    audioCtx = new AudioContext();
    const srcNode = audioCtx.createMediaElementSource(video);
    const dest = audioCtx.createMediaStreamDestination();
    srcNode.connect(dest);
    dest.stream.getAudioTracks().forEach((t) => stream.addTrack(t));
  } catch { /* silent reel */ }

  const mimeType = pickRecordingMime();
  const rec = new MediaRecorder(stream, { ...(mimeType ? { mimeType } : {}), videoBitsPerSecond: 8_000_000 });
  const chunks: Blob[] = [];
  rec.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };

  const total = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : Math.max(1, opts.durationS);
  const draw = () => {
    const vw = video.videoWidth || 1280, vh = video.videoHeight || 720;
    ctx.fillStyle = '#05080e'; ctx.fillRect(0, 0, W, H);
    const fill = Math.max(W / vw, H / vh);
    if (opts.mode === 'fill') {
      ctx.drawImage(video, (W - vw * fill) / 2, (H - vh * fill) / 2, vw * fill, vh * fill);
    } else {
      ctx.save(); ctx.filter = 'blur(36px) brightness(0.5) saturate(1.2)';
      ctx.drawImage(video, (W - vw * fill) / 2 - 40, (H - vh * fill) / 2 - 40, vw * fill + 80, vh * fill + 80);
      ctx.restore();
      const s = W / vw, dh = vh * s, y = (H - dh) / 2;
      ctx.drawImage(video, 0, y, W, dh);
      if (opts.title) {
        ctx.fillStyle = '#ffffff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.font = '700 64px Outfit, system-ui, sans-serif';
        ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = 18;
        ctx.fillText(fitText(ctx, opts.title, W - 140), W / 2, Math.max(120, y / 2));
        ctx.shadowBlur = 0;
      }
    }
    // gathrxr.com pill
    ctx.font = '600 40px Outfit, system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const label = 'gathrxr.com', tw = ctx.measureText(label).width + 64, py = H - 170;
    ctx.fillStyle = 'rgba(5, 11, 18, 0.62)'; roundRect(ctx, (W - tw) / 2, py - 38, tw, 76, 38); ctx.fill();
    ctx.fillStyle = '#ffffff'; ctx.fillText(label, W / 2, py + 2);
    opts.onProgress?.(Math.min(99, Math.round((video.currentTime / total) * 100)));
  };

  return await new Promise<ReelResult>((resolve, reject) => {
    let raf = 0, done = false;
    const vfc = (video as any).requestVideoFrameCallback?.bind(video);
    const loop = () => { if (done) return; draw(); raf = vfc ? vfc(loop) : requestAnimationFrame(loop); };
    const cleanup = () => {
      done = true;
      if (!vfc) cancelAnimationFrame(raf);
      video.pause(); audioCtx?.close().catch(() => {});
      stream.getTracks().forEach((t) => t.stop());
      URL.revokeObjectURL(url);
    };
    rec.onstop = () => {
      cleanup();
      if (opts.signal?.aborted) return reject(new DOMException('Cancelled', 'AbortError'));
      const mime = baseMime(rec.mimeType || mimeType);
      opts.onProgress?.(100);
      resolve({ blob: new Blob(chunks, { type: mime }), mime });
    };
    video.onended = () => { if (rec.state !== 'inactive') rec.stop(); };
    opts.signal?.addEventListener('abort', () => { if (rec.state !== 'inactive') rec.stop(); }, { once: true });
    draw();
    rec.start(250);
    audioCtx?.resume().catch(() => {});
    video.play().then(loop).catch((e) => { cleanup(); reject(e); });
  });
}

function fitText(ctx: CanvasRenderingContext2D, text: string, max: number): string {
  if (ctx.measureText(text).width <= max) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > max) t = t.slice(0, -1);
  return `${t}…`;
}
function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
}
